import { ArrowLeft, Trash2, ShoppingBag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import { useOrder } from '@/contexts/OrderContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';

const CartPage = () => {
  const { cart, updateQuantity, removeFromCart, getCartTotal, clearCart } = useCart();
  const { orderType, calculatePackagingCharges, getMinimumOrder } = useOrder();
  const navigate = useNavigate();

  const subtotal = getCartTotal();
  const packagingCharges = calculatePackagingCharges(cart);
  const total = subtotal + packagingCharges;
  const minimumOrder = getMinimumOrder();

  const handleRemove = (item) => {
    removeFromCart(item.id);
    toast.success(`${item.name} removed from cart`);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    if (subtotal < minimumOrder) {
      toast.error(`Minimum order amount is ₹${minimumOrder}`);
      return;
    }
    navigate('/checkout');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <header className="bg-gray-900/80 backdrop-blur-md border-b border-amber-500/30">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button onClick={() => navigate('/menu')} variant="ghost" className="text-amber-400 hover:bg-amber-500/10">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold text-amber-400 font-['Playfair_Display']">Your Cart</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {cart.length === 0 ? (
          <Card className="p-12 text-center space-y-4 bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500/30">
            <ShoppingBag className="w-16 h-16 mx-auto text-amber-400" />
            <h2 className="text-2xl font-bold text-amber-400">Your cart is empty</h2>
            <p className="text-gray-300">Add some delicious items from our menu</p>
            <Button onClick={() => navigate('/menu')} className="bg-gradient-to-r from-orange-500 to-red-600">
              Browse Menu
            </Button>
          </Card>
        ) : (
          <div className="space-y-6">
            <div className="space-y-3">
              {cart.map(item => (
                <Card key={item.id} className="p-4 bg-gradient-to-br from-gray-800 to-gray-900 border-2 border-amber-500/30">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-semibold text-amber-100">{item.name}</h3>
                      <p className="text-sm text-gray-400">₹{item.price} each</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-3 bg-gray-700/50 rounded-lg px-4 py-2">
                        <Button onClick={() => updateQuantity(item.id, item.quantity - 1)} variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-400">
                          -
                        </Button>
                        <span className="font-semibold text-lg text-white w-8 text-center">{item.quantity}</span>
                        <Button onClick={() => updateQuantity(item.id, item.quantity + 1)} variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-400">
                          +
                        </Button>
                      </div>
                      <div className="font-bold text-lg text-amber-400 w-20 text-right">
                        ₹{item.price * item.quantity}
                      </div>
                      <Button onClick={() => handleRemove(item)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <Card className="p-6 bg-gradient-to-br from-amber-500 to-orange-600 text-white border-0 shadow-xl">
              <div className="space-y-4">
                <div className="flex justify-between text-lg">
                  <span>Subtotal</span>
                  <span>₹{subtotal}</span>
                </div>
                <div className="flex justify-between text-lg">
                  <span>Packaging Charges</span>
                  <span>₹{packagingCharges}</span>
                </div>
                <div className="flex justify-between text-xl font-bold border-t-2 border-white/30 pt-4">
                  <span>Total</span>
                  <span>₹{total}</span>
                </div>
                {subtotal < minimumOrder && (
                  <p className="text-sm bg-red-500/20 border border-red-300 rounded p-2">
                    Minimum order: ₹{minimumOrder}. Add ₹{minimumOrder - subtotal} more
                  </p>
                )}
                <Button
                  onClick={handleCheckout}
                  disabled={subtotal < minimumOrder}
                  className="w-full bg-white text-orange-600 hover:bg-gray-100 font-bold text-lg py-6"
                >
                  Proceed to Checkout
                </Button>
                <Button onClick={() => { clearCart(); toast.success('Cart cleared'); }} variant="ghost" className="w-full text-white hover:bg-white/20">
                  Clear Cart
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default CartPage;
